import type { ChildProfile } from "@howcomet/shared";
import { AVATARS } from "@howcomet/shared";
import { usePublishedHeight } from "../bottom-bars";

export type Tab = "ask" | "sky" | "goal";

export function TopBar({
  profile,
  onGrownUps,
}: {
  profile: ChildProfile;
  onGrownUps: () => void;
}) {
  const avatar = AVATARS.find((a) => a.id === profile.avatarId)?.emoji ?? "⭐";

  return (
    <div className="safe-top flex items-center justify-between px-4 pb-3 pt-5">
      <div className="flex items-center gap-2">
        <span className="text-[26px]">{avatar}</span>
        <span className="disp text-[22px] text-cream">{profile.name}</span>
      </div>
      <div className="flex items-center gap-2">
        <div
          className="disp flex items-center gap-2 rounded-full bg-panel px-4 py-2 text-[18px] text-gold"
          aria-label={`${profile.stardust} stardust`}
        >
          ⭐ {profile.stardust}
        </div>
        <button
          onClick={onGrownUps}
          className="kidbtn rounded-full border border-cream/25 px-3 py-2 text-[13px] font-bold text-cream/55"
        >
          Grown-ups
        </button>
      </div>
    </div>
  );
}

/**
 * Three destinations, no more. No settings, no model picker, no modes — a kid
 * should never find a control that changes how the app behaves.
 */
export function TabBar({
  tab,
  setTab,
  fadingCount,
}: {
  tab: Tab;
  setTab: (tab: Tab) => void;
  fadingCount: number;
}) {
  // Publishes its own height, so the composer and the toast sit above whatever
  // this actually measures rather than what it measured on an iPad once.
  const barRef = usePublishedHeight<HTMLDivElement>("--tabbar-h");

  const items: { id: Tab; label: string; emoji: string; badge?: number }[] = [
    { id: "ask", label: "Ask", emoji: "💬" },
    { id: "sky", label: "My sky", emoji: "🌟", badge: fadingCount },
    { id: "goal", label: "My goal", emoji: "🎁" },
  ];

  return (
    /*
      The home-indicator inset is inside this one padding value rather than in a
      `.safe-bottom` class beside it, and that is the fix for a bug rather than a
      style preference. Two rules setting `padding-bottom` on one element is a
      collision, and the class was unlayered so it won every time — the `pb-4`
      that used to sit here had simply never applied.

      It has to stay padding, too: `publish` below measures this element with
      `offsetHeight`, which counts padding and ignores margin, and that number is
      what holds the composer, the toast and the scroll clearance above the bar.
      Expressed as one declaration there is nothing left to collide with and
      nothing left to under-measure.
    */
    <div
      ref={barRef}
      className="fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[calc(1rem_+_env(safe-area-inset-bottom))]"
    >
      <div className="flex gap-2 rounded-full bg-panel p-2 shadow-[0_-2px_30px_rgba(0,0,0,.5)]">
        {items.map((item) => {
          const active = tab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              aria-current={active ? "page" : undefined}
              /*
                `whitespace-nowrap` is the important one: without it "My sky"
                and "My goal" break onto two lines on a phone, which is what
                made this bar tall enough to swallow the composer. The smaller
                padding and type below 640px are what make them fit at all on a
                390px screen.
              */
              className={`kidbtn disp relative whitespace-nowrap rounded-full px-3.5 py-3 text-[15px] sm:px-6 sm:text-[17px] ${
                active ? "bg-gold text-night" : "text-cream/70"
              }`}
            >
              <span className="mr-1.5 sm:mr-2">{item.emoji}</span>
              {item.label}
              {!active && (item.badge ?? 0) > 0 && (
                <span className="absolute right-2.5 top-1.5 h-2.5 w-2.5 rounded-full bg-pink" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
